import React, { useState, useEffect } from "react";

import MoodInput from "./components/MoodInput";
import Disclaimer from "./components/Disclaimer";
import LocationInput from "./components/LocationInput";
import MusicList from "./components/MusicList";
import ErrorMessage from "./components/ErrorMessage";
import {
  getMusicByMood,
  getMusicByCoordinates,
  getMusicByCity,
} from "./services/musicService";
import "./App.css";

function App() {
  const [songs, setSongs] = useState([]);
  const [modelResponse, setModelResponse] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [recommendationType, setRecommendationType] = useState("");
  const [error, setError] = useState(null);
  const [songCount, setSongCount] = useState(5);
  const [nextPageUrl, setNextPageUrl] = useState(null);
  const [currentLocation, setCurrentLocation] = useState(null);
  const [user, setUser] = useState(null);
  const [fetched, setFetched] = useState(false);

  useEffect(() => {
    const token = sessionStorage.getItem("token");
    const userData = sessionStorage.getItem("user");
    if (token && userData) {
      setUser(JSON.parse(userData));
    }
  }, []);

  // Handle mood-based music recommendations
  const handleMoodSubmit = async (mood) => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await getMusicByMood(mood);
      // Extract the array of songs and the modelResponse from the response
      const songsArray = response.data?.data?.results || [];
      setSongs(songsArray);
      setRecommendationType(`Based on your emotion: "${mood}"`);
      const modelResp = response.data?.modelResponse;
      setModelResponse(modelResp || null);
    } catch (error) {
      console.error("Error fetching music by emotion:", error);
      setError(error.message || "Failed to fetch music by emotion.");
      setModelResponse(null);
    } finally {
      setIsLoading(false);
      setFetched(true);
    }
  };

  // Handle location-based music recommendations
  const handleLocationSubmit = async (locationData) => {
    setIsLoading(true);
    setError(null);
    setCurrentLocation(locationData);
    setModelResponse(null); // Clear any previous model response for location-based search

    try {
      let response;
      if (locationData.latitude && locationData.longitude) {
        response = await getMusicByCoordinates(
          locationData.latitude,
          locationData.longitude,
          songCount
        );
        setRecommendationType("Based on your current location coordinates");
      } else {
        response = await getMusicByCity(
          locationData.city,
          locationData.country,
          songCount
        );
        setRecommendationType(
          `Based on ${locationData.city}, ${locationData.country}`
        );
      }

      if (response && response.data && response.data.status === "SUCCESS") {
        const artists = response.data.data?.results || [];
        setSongs(artists);
        setNextPageUrl(response.data.data?.headers?.next || null);
        // Do not set error for empty results; MusicList will handle this.
        // if (artists.length === 0) {
        //   setError("No music found for this location.");
        // }
      } else {
        // Only set error if there is a real error message (not just empty/no results)
        if (
          response.data &&
          response.data.message &&
          response.data.message !== "No music found for this location."
        ) {
          setError(response.data.message);
        } else {
          setError(null); // Don't show an error for empty results
        }
      }
    } catch (error) {
      console.error("Error fetching music by location:", error);
      setError(error.message || "Failed to fetch music by location.");
    } finally {
      setIsLoading(false);
      setFetched(true);
    }
  };

  // Handle loading more songs (pagination)
  const handleLoadMore = async () => {
    if (!nextPageUrl || !currentLocation) return;

    setIsLoading(true);
    const newSongCount = songCount + 5;
    setSongCount(newSongCount);

    try {
      let response;
      if (currentLocation.latitude && currentLocation.longitude) {
        response = await getMusicByCoordinates(
          currentLocation.latitude,
          currentLocation.longitude,
          newSongCount
        );
      } else {
        response = await getMusicByCity(
          currentLocation.city,
          currentLocation.country,
          newSongCount
        );
      }

      if (response && response.data && response.data.status === "SUCCESS") {
        const newArtists = response.data.data?.results || [];
        setSongs((prevSongs) => [...prevSongs, ...newArtists]);
        setNextPageUrl(response.data.data?.headers?.next || null);
      } else {
        setError("Failed to load more music.");
      }
    } catch (error) {
      console.error("Error loading more music:", error);
      setError("Failed to load more music. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  if (!user) {
    return null; // Or a loading spinner if you prefer
  }

  return (
    <div className="App">
      <Disclaimer />
      <div className="main-content-area">
        <main>
          <div className="input-section">
            <MoodInput onSubmitMood={handleMoodSubmit} isLoading={isLoading} />
            <LocationInput
              onSubmitLocation={handleLocationSubmit}
              isLoading={isLoading}
            />
          </div>
          {error && <ErrorMessage message={error} />}
          <MusicList
            songs={songs}
            title={recommendationType}
            fetched={fetched}
            onLoadMore={handleLoadMore}
            hasNextPage={!!nextPageUrl}
            isLoading={isLoading}
            modelResponse={modelResponse}
          />
        </main>
      </div>
    </div>
  );
}

export default App;
